import { useState } from 'react';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
}

interface SharedFAQProps {
  faqs: FAQItem[];
  title?: string;
  subtitle?: string;
  bgClass?: string;
}

export default function SharedFAQ({
  faqs,
  title = 'Frequently Asked',
  subtitle = 'Answers optimized for voice search and AI assistants.',
  bgClass = 'bg-white',
}: SharedFAQProps) {
  const [openId, setOpenId] = useState<number | null>(faqs[0]?.id ?? null);

  return (
    <section className={`py-24 ${bgClass}`}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-14">
          {/* Sticky left */}
          <div className="lg:col-span-2">
            <div className="lg:sticky lg:top-28">
              <p className="text-[#FF6B35] text-sm font-bold uppercase tracking-widest mb-3">Got Questions?</p>
              <h2 className="text-4xl md:text-5xl font-black text-stone-900 leading-tight mb-6">
                {title.split(' ').slice(0, -1).join(' ')}<br />
                <span className="text-[#0B5C4D]">{title.split(' ').slice(-1)}</span>
              </h2>
              {subtitle && (
                <p className="text-stone-500 text-base leading-relaxed mb-8">{subtitle}</p>
              )}
              <div className="bg-[#F5EFE6] rounded-2xl p-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#0B5C4D] flex items-center justify-center flex-shrink-0">
                    <i className="ri-customer-service-line text-white text-lg" />
                  </div>
                  <div>
                    <p className="text-stone-800 font-bold text-sm mb-1">Still have questions?</p>
                    <p className="text-stone-500 text-xs">Our team answers within 24 hours.</p>
                    <a
                      href="mailto:hello@phixdoctor.com"
                      className="inline-block mt-3 text-[#0B5C4D] text-xs font-bold hover:text-[#FF6B35] transition-colors cursor-pointer"
                    >
                      Contact Us →
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Accordion */}
          <div className="lg:col-span-3 space-y-0">
            {faqs.map((faq) => (
              <div key={faq.id} className="border-b border-stone-100">
                <button
                  className="w-full flex items-start justify-between gap-4 py-6 text-left cursor-pointer group"
                  onClick={() => setOpenId(openId === faq.id ? null : faq.id)}
                >
                  <span className="text-stone-900 font-bold text-base group-hover:text-[#0B5C4D] transition-colors pr-4">
                    {faq.question}
                  </span>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${openId === faq.id ? 'bg-[#FF6B35] rotate-45' : 'bg-stone-100'}`}>
                    <i className={`ri-add-line text-sm ${openId === faq.id ? 'text-white' : 'text-stone-500'}`} />
                  </div>
                </button>
                {openId === faq.id && (
                  <div className="pb-6 pr-12">
                    <p className="text-stone-600 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
